import { ReactNode } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { api } from '@/api';
import { useUser } from '@/core/user';
import { Loading } from '@/shared/ui';
import { generateAppRoute } from '@/shared/util';

import { StudyGuardProvider } from './study-guard.provider';
import { filterImagingServices } from './study-guard.util';

interface StudyGuardProps {
  children: ReactNode;
}

export function StudyGuard({ children }: StudyGuardProps) {
  const { studyId } = useParams<{ studyId: string }>();
  const { user } = useUser();

  if (!studyId) {
    return <Navigate replace to={generateAppRoute('NOT_FOUND')} />;
  }

  const {
    data: radiologistScan,
    isError: isRadiologistScanError,
    isLoading: isRadiologistScanLoading,
  } = api.apps.study.query.useGetRadiologistScanDetails({
    payload: { studyId },
  });

  const {
    data: patients,
    isError: patientsError,
    isLoading: isPatientsLoading,
  } = api.pii.patient.query.useGetPatient({
    payload: { params: { user_id: radiologistScan?.patientID } },
  });

  const reportEnabled = !!studyId && !!user.user_id && !!patients?.[0]?.gender;

  const {
    data: report,
    isError: isReportError,
    isLoading: isReportLoading,
  } = api.fct.report.query.useGetReportStudy(
    {
      payload: { params: { patient_gender: patients?.[0].gender, user_id: user.user_id }, studyId },
    },
    { enabled: reportEnabled },
  );

  if (isRadiologistScanLoading || isPatientsLoading || (reportEnabled && isReportLoading)) {
    return <Loading />;
  }

  if (
    isRadiologistScanError ||
    patientsError ||
    !radiologistScan ||
    !patients ||
    patients.length <= 0 ||
    (reportEnabled && (isReportError || !report))
  ) {
    return <Navigate replace to={generateAppRoute('NOT_FOUND')} />;
  }

  return (
    <StudyGuardProvider
      patient={patients[0]}
      radiologistScan={{
        ...radiologistScan,
        poetryMetadata: {
          ...(radiologistScan.poetryMetadata ?? {}),
          ...filterImagingServices(radiologistScan.poetryMetadata ?? {}),
        },
      }}
      report={reportEnabled ? report : undefined}
      studyId={studyId}
    >
      {children}
    </StudyGuardProvider>
  );
}
